import type { CareBoard, CareCardRow, DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// Pure model for the Care desk — the remainder-first meal/care view (feat/
// care-desk K5, NEX-457; the same grammar as hotel-desk/desk-model.ts). ONE
// classifier decides every PNR's lane over the roster walk × the K4 care board,
// so the goal bar, the work queue and the KYC strip can never disagree. The
// operator's goal ends the shift: every entitled PNR holds a LIVE card or a
// recorded counter outcome — and the desk never turns green while a passenger
// got nothing (BLOCKED_KYC blocks settlement).

export type CareLane =
  /** Live spendable card — done. */
  | 'ISSUED'
  /** Card issuance FAILED — worked first (retry is safe). */
  | 'FAILED'
  /** Entitled, no card yet — the amber remainder. */
  | 'NEEDS_ISSUE'
  /** Discretionary second look (violet): a live card PLUS a declared
   *  AIRPORT_WAIT with a suggested grant the operator may act on. NEVER counted
   *  in the amber remainder. */
  | 'ATTENTION'
  /** No card AND the lead has no KYC token — a collapsed strip, but it STILL
   *  blocks allSettled: the desk never turns green while a passenger got nothing. */
  | 'BLOCKED_KYC'
  /** Declared careOutcome (counter voucher / handled outside) — the story ended first. */
  | 'HANDLED_OUTSIDE'
  /** Not entitled to care — off this desk. */
  | 'NOT_ENTITLED';

export interface CareDeskRow {
  row: DisruptionPnrGroupRow;
  lane: CareLane;
  /** The K4 board's card row for this group, when the board carries one. */
  card?: CareCardRow;
}

export interface CareDeskState {
  rows: CareDeskRow[];
  /** Worked lanes only (FAILED → NEEDS_ISSUE → ATTENTION), pax-weighted worst first. */
  queue: CareDeskRow[];
  /** The KYC strip rows (worst first) — visible, never in the way. */
  blocked: CareDeskRow[];
  counts: Record<CareLane, { pnrs: number; pax: number }>;
  /** PNRs the goal covers (everything except NOT_ENTITLED). */
  goalPnrs: number;
  goalPax: number;
  /** The amber remainder: FAILED + NEEDS_ISSUE (ATTENTION is discretionary — never counted). */
  remainderPnrs: number;
  remainderPax: number;
  /** True when every entitled PNR holds a live card or a recorded outcome AND
   *  nobody is KYC-blocked. An empty walk / zero entitled is NEVER settled. */
  allSettled: boolean;
}

/** Wallet statuses that mean "a live card exists" (PENDING = being cut — issued,
 *  not re-issuable; the WS flips it to ACTIVE). `ISSUED` is the BFF's degraded
 *  "card exists, balances not read". */
const LIVE_CARD_STATUSES = new Set(['ACTIVE', 'PENDING', 'ISSUED']);

function hasLiveCard(row: DisruptionPnrGroupRow, card?: CareCardRow): boolean {
  if (card) return LIVE_CARD_STATUSES.has(card.status);
  // Degraded fallback (no board / board row missing): the cases denorm says a
  // card was confirmed for the group.
  return row.mealVoucher?.issued === true;
}

/** Is this PNR on the care desk at all? Membership (frozen §4): a MEAL_VOUCHER
 *  entitlement ∨ a live card for the group ∨ a declared AIRPORT_WAIT ∨ a
 *  mealVoucher (which may be projected from walletAllocation — NEX-613). */
export function isCareEntitled(row: DisruptionPnrGroupRow, card?: CareCardRow): boolean {
  if (row.accommodationKind === 'MEAL_VOUCHER') return true;
  if (row.accommodationOutcome?.outcome === 'AIRPORT_WAIT') return true;
  if (row.mealVoucher) return true;
  return hasLiveCard(row, card);
}

export function careLaneOf(row: DisruptionPnrGroupRow, card?: CareCardRow): CareLane {
  if (!isCareEntitled(row, card)) return 'NOT_ENTITLED';
  // A declared careOutcome ends the group's card story FIRST — regardless of
  // what the wallet holds (the operator settled it at the counter).
  if (row.careOutcome) return 'HANDLED_OUTSIDE';
  if (card?.status === 'FAILED') return 'FAILED';
  if (hasLiveCard(row, card)) {
    // Discretionary attention ON TOP of a live card: the group declined the
    // hotel, waits at the airport, and the wait window suggests a top-up.
    if (row.accommodationOutcome?.outcome === 'AIRPORT_WAIT' && row.careSuggestedGrant) {
      return 'ATTENTION';
    }
    return 'ISSUED';
  }
  if (card?.status === 'BLOCKED_KYC') return 'BLOCKED_KYC';
  // Anything else — NOT_ISSUED, a voided/disabled card, or no board row at all —
  // is an entitled group still owed a live card.
  return 'NEEDS_ISSUE';
}

const QUEUE_ORDER: Partial<Record<CareLane, number>> = {
  FAILED: 0,
  NEEDS_ISSUE: 1,
  ATTENTION: 2,
};

function worstFirst(a: CareDeskRow, b: CareDeskRow): number {
  return (
    QUEUE_ORDER[a.lane]! - QUEUE_ORDER[b.lane]! ||
    (b.row.paxCount ?? 0) - (a.row.paxCount ?? 0) ||
    a.row.pnrLabel.localeCompare(b.row.pnrLabel)
  );
}

export function buildCareDesk(
  rows: DisruptionPnrGroupRow[],
  board: CareBoard | null | undefined,
): CareDeskState {
  const cardBySub = new Map<string, CareCardRow>(
    (board?.cards ?? []).map((c) => [c.subCaseUrn, c]),
  );
  const deskRows: CareDeskRow[] = rows.map((row) => {
    const card = cardBySub.get(row.subCaseUrn);
    return { row, lane: careLaneOf(row, card), ...(card ? { card } : {}) };
  });

  const counts: CareDeskState['counts'] = {
    ISSUED: { pnrs: 0, pax: 0 },
    FAILED: { pnrs: 0, pax: 0 },
    NEEDS_ISSUE: { pnrs: 0, pax: 0 },
    ATTENTION: { pnrs: 0, pax: 0 },
    BLOCKED_KYC: { pnrs: 0, pax: 0 },
    HANDLED_OUTSIDE: { pnrs: 0, pax: 0 },
    NOT_ENTITLED: { pnrs: 0, pax: 0 },
  };
  for (const d of deskRows) {
    counts[d.lane].pnrs += 1;
    counts[d.lane].pax += d.row.paxCount ?? 0;
  }

  const queue = deskRows.filter((d) => d.lane in QUEUE_ORDER).sort(worstFirst);
  const blocked = deskRows
    .filter((d) => d.lane === 'BLOCKED_KYC')
    .sort(
      (a, b) =>
        (b.row.paxCount ?? 0) - (a.row.paxCount ?? 0) ||
        a.row.pnrLabel.localeCompare(b.row.pnrLabel),
    );

  const goalPnrs = deskRows.length - counts.NOT_ENTITLED.pnrs;
  const goalPax =
    deskRows.reduce((s, d) => s + (d.row.paxCount ?? 0), 0) - counts.NOT_ENTITLED.pax;

  return {
    rows: deskRows,
    queue,
    blocked,
    counts,
    goalPnrs,
    goalPax,
    remainderPnrs: counts.FAILED.pnrs + counts.NEEDS_ISSUE.pnrs,
    remainderPax: counts.FAILED.pax + counts.NEEDS_ISSUE.pax,
    allSettled:
      goalPnrs > 0 &&
      counts.FAILED.pnrs === 0 &&
      counts.NEEDS_ISSUE.pnrs === 0 &&
      counts.BLOCKED_KYC.pnrs === 0,
  };
}

/** "US$25" / "BRL 120" — the entitlement's face value, operator-friendly. */
export function formatEntitlement(amount: number, currency: string): string {
  const rounded = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return currency === 'USD' ? `US$${rounded}` : `${currency} ${rounded}`;
}

/** "$25.00" — a MINOR-unit balance for the card strip (money is minor on the wire). */
export function formatMinor(minor: number, currency: string): string {
  const major = (minor / 100).toFixed(2);
  return currency === 'USD' ? `$${major}` : `${currency} ${major}`;
}

/** The i18n key (+ interpolation params) for a row's "why am I here" line.
 *  This file holds KEYS, never language — the rendering component calls `t()`
 *  in the `care` namespace. */
export interface CareLaneReason {
  key: string;
  params?: { amount: string };
}

/** Human reason line per lane (the row's "why am I here"), as a `care` key. */
export function careLaneReason(d: CareDeskRow): CareLaneReason | null {
  const { row } = d;
  const waiting = row.accommodationOutcome?.outcome === 'AIRPORT_WAIT';
  switch (d.lane) {
    case 'FAILED':
      return { key: 'reason.failed' };
    case 'NEEDS_ISSUE':
      if (row.mealVoucher) {
        return {
          key: 'reason.needsIssue.entitled',
          params: {
            amount: formatEntitlement(row.mealVoucher.amountUsd, row.mealVoucher.currency),
          },
        };
      }
      return { key: waiting ? 'reason.needsIssue.waiting' : 'reason.needsIssue.generic' };
    case 'ATTENTION':
      return row.careSuggestedGrant
        ? {
            key: 'reason.attention.suggested',
            params: {
              amount: formatEntitlement(
                row.careSuggestedGrant.amount,
                row.careSuggestedGrant.currency,
              ),
            },
          }
        : { key: 'reason.attention.waiting' };
    case 'BLOCKED_KYC':
      return { key: 'reason.blockedKyc' };
    case 'HANDLED_OUTSIDE':
      return row.careOutcome?.outcome === 'COUNTER_VOUCHER'
        ? { key: 'reason.handledOutside.counterVoucher' }
        : { key: 'reason.handledOutside.other' };
    case 'ISSUED':
      return d.card?.pendingTopUp ? { key: 'reason.issued.settling' } : { key: 'reason.issued.live' };
    default:
      return null;
  }
}
